import { useState } from 'react';
import {
  ColumnFiltersState,
  filterFns,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  PaginationState,
  Row,
  SortingState,
  TableMeta,
  TableOptions,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import type { MergeExclusive } from 'type-fest';

import { DEFAULT_PAGE_SIZE } from './constants';
import { ColumnDef, TableInstance } from './types';

const GLOBAL_FILTER_FN: keyof typeof filterFns = 'includesString';

export type SimpleTableHookProps<TRow extends object> = {
  /**
   * List of columns to render
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- We want to allow columns with all value types
  columns: ColumnDef<TRow, any>[];
  /**
   * List of data to render per row
   */
  rowData: TRow[] | undefined;
  /**
   * amount of items per page
   */
  pageSize?: number;
  /**
   * activates pagination
   */
  hasPagination?: boolean;
  /**
   * activates searching
   */
  hasFilter?: boolean;
  /**
   * Set of columns to be initially visible/hidden
   */
  initialColumnVisibility?: VisibilityState;
  /**
   * Manually control column visibility state outside useSimpleTable
   *
   * In general, you should not need this - you should be able to combine `initialColumnVisibility`
   * and `table.setColumnVisibility` to achieve what you need
   *
   * This is only useful if you need to access the setter before the call to `useSimpleTable`
   */
  manualColumnVisibility?: {
    columnVisibility: VisibilityState;
    onSetColumnVisibility: OnChangeFn<VisibilityState>;
  };
  /**
   * Set of columns to initially sort by
   */
  initialSortBy?: SortingState;
  /**
   * deactivates react-table pagination
   */
  useManualPagination?:
    | { enabled: false }
    | {
        enabled: true;
        pageCount: number;
        pageSize: number;
        pageIndex: number;
        onPaginationChange: OnChangeFn<PaginationState>;
      };
  /**
   * deactivates react-table column sorting
   */
  useManualSort?:
    | { enabled: false }
    | {
        enabled: true;
        onSetSorting: OnChangeFn<SortingState>;
        sorting: SortingState;
      };
  /**
   * deactivates react-table searching
   */
  useManualFiltering?:
    | {
        enabled: false;
      }
    | { enabled: true; onFilterChange: OnChangeFn<string | undefined>; filter: string | undefined };
  /**
   * handles expanded rows configuration
   */
  useExpanded?:
    | { enabled: false }
    | ({
        enabled: true;

        /**
         * Sets the rows that are to be expanded on load
         */
        initialExpanded?: Record<string, boolean>;
      } & MergeExclusive<
        {
          /**
           * accessor function for sub-rows
           */
          getSubRows: (row: TRow) => TRow[] | undefined;
        },
        {
          /**
           * function to enable/disable row expanding
           */
          getRowCanExpand: (row: Row<TRow>) => boolean;
        }
      >);

  /**
   * Whether the use can turn of sorting by the currently sorted column.
   * By default, this is enabled if multi sort is enabled
   */
  enableSortingRemoval?: boolean;

  /**
   * Enables multi-sorting for this table
   *
   * @default false
   */
  enableMultiSort?: boolean;

  /**
   * Enables sorting for this table
   *
   * @default true
   */
  enableSorting?: boolean;
  initialColumnFilters?: ColumnFiltersState;
  meta?: TableMeta<TRow>;
  getRowId?: (originalRow: TRow, index: number, parent?: Row<TRow>) => string;
};

// Used as a fallback when `rowData` is undefined
// A module constant rather than a field default so that we always get the same array
// New array each render gives an infinite loop, JS evaluates default parameters at call time
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/Default_parameters#evaluated_at_call_time
const NO_DATA_FALLBACK: never[] = [];

export const useSimpleTable = <TRow extends object>({
  columns,
  rowData = NO_DATA_FALLBACK,
  hasPagination = false,
  hasFilter = false,
  pageSize = DEFAULT_PAGE_SIZE,
  initialColumnVisibility = {},
  manualColumnVisibility,
  initialSortBy = [],
  useManualPagination = {
    enabled: false,
  },
  useManualSort = {
    enabled: false,
  },
  useManualFiltering = {
    enabled: false,
  },
  useExpanded = {
    enabled: false,
  },
  enableSortingRemoval,
  enableMultiSort = false,
  enableSorting = true,
  initialColumnFilters = [],
  meta,
  getRowId,
}: SimpleTableHookProps<TRow>): TableInstance<TRow> => {
  const [sorting, setSorting] = useState<SortingState>(initialSortBy);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(initialColumnVisibility);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialColumnFilters);

  const options: TableOptions<TRow> = {
    columns,
    data: rowData,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize,
      },
    },
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    enableMultiSort,
    enableSorting,
    enableSortingRemoval: enableSortingRemoval === undefined ? enableMultiSort : enableSortingRemoval,
    /**
     * Removes requirement to hold shift while clicking column header to get multi sort behaviour
     */
    isMultiSortEvent: () => !!enableSorting && !!enableMultiSort,
    meta,
    getRowId,
  };

  if (manualColumnVisibility) {
    options.state = {
      ...options.state,
      columnVisibility: manualColumnVisibility.columnVisibility,
    };
    options.onColumnVisibilityChange = manualColumnVisibility.onSetColumnVisibility;
  } else {
    options.state = {
      ...options.state,
      columnVisibility,
    };
    options.onColumnVisibilityChange = setColumnVisibility;
  }

  if (hasPagination) {
    if (useManualPagination.enabled) {
      options.manualPagination = true;
      options.pageCount = useManualPagination.pageCount;
      options.onPaginationChange = useManualPagination.onPaginationChange;
      options.state = {
        ...options.state,
        pagination: {
          pageIndex: useManualPagination.pageIndex,
          pageSize: useManualPagination.pageSize,
        },
      };
    } else {
      options.getPaginationRowModel = getPaginationRowModel();
    }
  }

  const resetToFirstPage = () => {
    if (useManualPagination.enabled) {
      useManualPagination.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    }
    // react-table automatically resets to first page by default if not doing manual pagination
  };

  if (useManualSort.enabled) {
    options.manualSorting = true;
    options.onSortingChange = (updaterOrValue) => {
      resetToFirstPage();
      useManualSort.onSetSorting(updaterOrValue);
    };
    options.state = {
      ...options.state,
      sorting: useManualSort.sorting,
    };
  } else {
    options.getSortedRowModel = getSortedRowModel();
    options.onSortingChange = setSorting;
    options.state = {
      ...options.state,
      sorting,
    };
  }

  if (useManualFiltering.enabled) {
    options.manualFiltering = true;
    options.onGlobalFilterChange = (updaterOrValue) => {
      resetToFirstPage();
      useManualFiltering.onFilterChange(updaterOrValue);
    };
    options.state = {
      ...options.state,
      globalFilter: useManualFiltering.filter,
    };
  } else if (hasFilter) {
    options.onGlobalFilterChange = setGlobalFilter;
    options.globalFilterFn = GLOBAL_FILTER_FN;
    options.getFilteredRowModel = getFilteredRowModel();
    options.onColumnFiltersChange = setColumnFilters;
    options.state = {
      ...options.state,
      globalFilter,
      columnFilters,
    };
  }

  if (useExpanded.enabled) {
    options.getExpandedRowModel = getExpandedRowModel();

    options.initialState = {
      ...options.initialState,
      expanded: useExpanded.initialExpanded ? useExpanded.initialExpanded : undefined,
    };

    if (useExpanded.getRowCanExpand) {
      options.getRowCanExpand = useExpanded.getRowCanExpand;
    }

    if (useExpanded.getSubRows) {
      options.getSubRows = useExpanded.getSubRows;
    }
  }

  const table = useReactTable(options);

  return { ...table, rowCount: table.getFilteredRowModel().rows.length };
};
